import {
  useState,
  useRef,
  useEffect,
  type ChangeEvent,
  type RefObject,
} from 'react';
import clsx from 'clsx';
import type { DataType, TableInputDefinition } from '@arcanum-hq/shared-types';
import { Button } from '@/components/ui/Button';
import { formatCurrency, formatPercent, formatNumber } from '@/lib/format';
import { useTableInputsEdit } from '@/hooks/use-table-inputs-edit';
import {
  hydrateTableGrid,
  buildWritesFromGrid,
  buildDisplayRows,
  type HydratedRow,
} from './hydrate-table-grid';

/**
 * `<TableGridEditor>` — the reusable editable table grid (table-input-ux
 * immutable-history rework, 2026-06-08).
 *
 * Extracted from `<TableFillProposal>` so the SAME editor backs two surfaces:
 *
 *   1. The live `propose_table_fill` proposal card (seeded from the agent's
 *      `overlayCells` overlay).
 *   2. The re-edit card spawned at the end of the thread when the user clicks
 *      "Edit" on a `[table-edit:*]` receipt (`overlayCells` omitted → seeds
 *      from the current model values).
 *
 * On Apply it writes the full grid via `useTableInputsEdit` → `POST
 * /api/conversations/:id/table-edit`, which appends a `[table-edit:*]`
 * SystemMessage that renders as a fresh receipt at the end of the thread. The
 * editor itself is transient — `onApplied` lets the host dismiss it once the
 * receipt lands.
 *
 * ## D1 (numeric-only v1):
 * Non-numeric columns (date / boolean / location) render READ-ONLY (visible
 * but not editable; excluded from `writes`).
 *
 * ## D-extra (editable aggregate total):
 * When `TableInputDefinition.aggregates[]` has a `sum`-type aggregate, an
 * editable total header is shown. Editing it re-distributes proportionally
 * across the rows' target column.
 */

interface TableGridEditorProps {
  /** Full schema for the table being edited. */
  tableDef: TableInputDefinition;
  /** Conversation ID for the direct-write path. */
  conversationId: string;
  /**
   * Current input provenance from `useConversationViews().inputs` — supplies
   * current model values (and the schema-default fallback per cell).
   */
  currentInputs?:
    | Record<string, { value: unknown; source?: string }>
    | undefined;
  /**
   * Agent-proposed overlay (`propose_table_fill.cells`). When omitted the grid
   * seeds purely from current model values (re-edit path).
   */
  overlayCells?: Record<string, Record<string, number | string | boolean>>;
  /** Title shown in the editor header. */
  title: string;
  /** Optional sub-line under the title. */
  subtitle?: string;
  /** Optional rationale ("Basis: …") line. */
  rationale?: string;
  /** Apply button label. */
  applyLabel?: string;
  /** Dismiss/cancel button label. */
  dismissLabel?: string;
  /** Called after a successful Apply (host clears the editor). */
  onApplied?: () => void;
  /** Called when the user dismisses the editor without applying. */
  onDismiss?: () => void;
  /** Auto-focus the first editable cell + scroll into view on mount. */
  autoFocus?: boolean;
  /** Test id for the editor root. */
  testId?: string;
  /**
   * When true, the Apply/Submit button is disabled (in addition to the
   * isPending guard). Used by `<InteractionSurface>` to honor `billingReadOnly`
   * — the caller is responsible for a matching label (e.g. "… (limit reached)").
   */
  submitDisabled?: boolean;
}

export function TableGridEditor({
  tableDef,
  conversationId,
  currentInputs,
  overlayCells,
  title,
  subtitle,
  rationale,
  applyLabel = 'Apply to model',
  dismissLabel = 'Dismiss',
  onApplied,
  onDismiss,
  autoFocus = false,
  testId = 'table-grid-editor',
  submitDisabled = false,
}: TableGridEditorProps) {
  const [applyError, setApplyError] = useState<string | null>(null);
  // editedValues: user overrides keyed by rowLabel → colKey → number
  const [editedValues, setEditedValues] = useState<
    Record<string, Record<string, number>>
  >({});
  // cellDrafts: in-progress RAW text per cell so a field can be empty or
  // mid-typed (e.g. "" while deleting the last digit, or "1." before "1.5").
  // editedValues stays the committed numeric source; the input prefers the
  // draft for display when one exists. (user feedback 2026-06-09: backspace
  // couldn't clear the final digit because empty was swallowed.)
  const [cellDrafts, setCellDrafts] = useState<
    Record<string, Record<string, string>>
  >({});
  // editedTotal: user-edited aggregate total (for D-extra re-distribution)
  const [editedTotal, setEditedTotal] = useState<number | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const firstInputRef = useRef<HTMLInputElement | null>(null);

  const tableInputsEdit = useTableInputsEdit();

  const grid = hydrateTableGrid(tableDef, currentInputs, overlayCells);

  // Auto-focus the first editable cell + scroll the editor into view on mount
  // (re-edit card spawns at the end of the thread — the user should land on it).
  useEffect(() => {
    if (!autoFocus) return;
    rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    firstInputRef.current?.focus();
    // Mount-once seed-focus; deliberately no deps beyond autoFocus.
  }, [autoFocus]);

  // ── Aggregate total helpers (D-extra) ─────────────────────────────────────

  const sumAggregate = tableDef.aggregates.find((a) => a.aggregation === 'sum');
  const sumTargetCol = sumAggregate?.targetColumn;

  function computeCurrentTotal(): number {
    if (!sumTargetCol) return 0;
    return grid.rows.reduce((acc, row) => {
      const edited = editedValues[row.rowLabel]?.[sumTargetCol];
      if (edited !== undefined) return acc + edited;
      const cell = row.cells.find((c) => c.colKey === sumTargetCol);
      if (typeof cell?.displayValue === 'number')
        return acc + cell.displayValue;
      return acc;
    }, 0);
  }

  const currentTotal = editedTotal ?? computeCurrentTotal();

  function handleTotalChange(rawValue: string) {
    const parsed = Number.parseFloat(rawValue);
    if (!Number.isFinite(parsed) || parsed <= 0 || !sumTargetCol) return;

    const prevTotal = computeCurrentTotal();
    if (prevTotal === 0) return;

    // Re-distribute proportionally across all rows' target column
    const newEdited = { ...editedValues };
    for (const row of grid.rows) {
      const cell = row.cells.find((c) => c.colKey === sumTargetCol);
      if (!cell?.editable) continue;
      const prevVal =
        editedValues[row.rowLabel]?.[sumTargetCol] ??
        (typeof cell.displayValue === 'number' ? cell.displayValue : 0);
      const newVal = Math.round((prevVal / prevTotal) * parsed);
      newEdited[row.rowLabel] = {
        ...(newEdited[row.rowLabel] ?? {}),
        [sumTargetCol]: newVal,
      };
    }
    setEditedValues(newEdited);
    setEditedTotal(parsed);
    // Drop any in-progress drafts in the redistributed column so the cells
    // render the new proportional numbers rather than a stale typed draft.
    setCellDrafts((prev) => {
      const next: Record<string, Record<string, string>> = {};
      for (const rowLabel of Object.keys(prev)) {
        const { [sumTargetCol]: _dropped, ...rest } = prev[rowLabel] ?? {};
        if (Object.keys(rest).length > 0) next[rowLabel] = rest;
      }
      return next;
    });
  }

  // ── Cell edit handler ──────────────────────────────────────────────────────

  function handleCellEdit(rowLabel: string, colKey: string, value: number) {
    setEditedValues((prev) => ({
      ...prev,
      [rowLabel]: { ...(prev[rowLabel] ?? {}), [colKey]: value },
    }));
    if (colKey === sumTargetCol) {
      setEditedTotal(null); // will recompute from cells
    }
  }

  function handleNumericChange(rowLabel: string, colKey: string, raw: string) {
    // Always track the raw text so the field reflects exactly what's typed —
    // including an empty string (the user backspaced every digit) or an
    // intermediate form like "1." before "1.5".
    setCellDrafts((prev) => ({
      ...prev,
      [rowLabel]: { ...(prev[rowLabel] ?? {}), [colKey]: raw },
    }));
    // A cleared numeric field commits as 0 — an empty number means zero, not
    // the previous value (user feedback 2026-06-09). The draft above keeps the
    // input visually empty while editing; editedValues holds 0 for submit.
    if (raw === '') {
      handleCellEdit(rowLabel, colKey, 0);
      return;
    }
    const parsed = Number.parseFloat(raw);
    if (Number.isFinite(parsed)) handleCellEdit(rowLabel, colKey, parsed);
  }

  // ── Apply handler ───────────────────────────────────────────────────────────

  function handleApply() {
    setApplyError(null);
    const writes = buildWritesFromGrid(grid.rows, editedValues);
    const displayRows = buildDisplayRows(grid.rows, editedValues);

    tableInputsEdit.mutate(
      { conversationId, table: tableDef.name, writes, displayRows },
      {
        onSuccess: () => {
          onApplied?.();
        },
        onError: (err) => {
          setApplyError(
            err.message ?? 'Failed to apply table inputs. Please try again.'
          );
        },
      }
    );
  }

  const isPending = tableInputsEdit.isPending;

  return (
    <div
      ref={rootRef}
      data-testid={testId}
      data-table={tableDef.name}
      className="space-y-2"
    >
      {/* Header */}
      <div className="space-y-1">
        <p className="text-micro font-mono font-semibold tracking-[1.2px] uppercase text-text-muted">
          {title}
        </p>
        {subtitle && (
          <p className="text-xs font-mono text-text-bright/80">{subtitle}</p>
        )}
        {rationale && (
          <p className="text-sm text-text-primary">
            <span className="text-text-muted">Basis: </span>
            {rationale}
          </p>
        )}
      </div>

      {/* Aggregate total editor (D-extra) */}
      {sumAggregate && (
        <div className="flex items-center gap-2 py-1 border-b border-panel-border/50">
          <span className="text-xs font-mono text-text-muted">
            {sumAggregate.displayName}:
          </span>
          <span className="text-xs font-mono text-text-muted">Σ</span>
          <input
            type="number"
            value={currentTotal}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              handleTotalChange(e.target.value)
            }
            data-testid="table-fill-total-input"
            className={clsx(
              'w-20 bg-transparent border border-panel-border/40 rounded-[2px] px-1.5 py-0.5',
              'text-xs font-mono tabular-nums text-text-bright',
              'focus:outline-none focus:border-accent/50 focus:bg-panel-bg'
            )}
          />
        </div>
      )}

      {/* Grid table */}
      <div className="overflow-x-auto">
        <table className="text-xs font-mono tabular-nums">
          <thead>
            <tr className="border-b border-panel-border">
              <th className="px-2 py-1.5 text-left text-text-muted font-semibold uppercase tracking-[0.5px]" />
              {tableDef.columns.map((c) => (
                <th
                  key={c.key}
                  className="px-2 py-1.5 text-left text-text-muted font-semibold uppercase tracking-[0.5px]"
                >
                  {c.displayName}
                  {!isNumericDataType(c.dataType) && (
                    <span className="ml-1 text-text-dim normal-case tracking-normal font-normal">
                      (view)
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {grid.rows.map((row, rowIdx) =>
              renderEditableRow(
                row,
                rowIdx,
                editedValues,
                cellDrafts,
                handleNumericChange,
                firstInputRef
              )
            )}
          </tbody>
        </table>
      </div>

      {/* Error message */}
      {applyError && (
        <p
          className="text-xs font-mono text-danger"
          data-testid="table-fill-error"
        >
          {applyError}
        </p>
      )}

      {/* Action buttons */}
      <div className="flex gap-2 pt-1">
        <Button
          variant="primary"
          onClick={handleApply}
          disabled={isPending || submitDisabled}
          data-testid="table-fill-confirm"
        >
          {isPending ? 'Applying…' : applyLabel}
        </Button>
        <Button
          variant="ghost"
          onClick={() => onDismiss?.()}
          disabled={isPending}
          data-testid="table-fill-cancel"
        >
          {dismissLabel}
        </Button>
      </div>
    </div>
  );
}

// ── Render helper ───────────────────────────────────────────────────────────────

function renderEditableRow(
  row: HydratedRow,
  rowIdx: number,
  editedValues: Record<string, Record<string, number>>,
  cellDrafts: Record<string, Record<string, string>>,
  onNumericChange: (rowLabel: string, colKey: string, raw: string) => void,
  firstInputRef: RefObject<HTMLInputElement | null>
) {
  return (
    <tr key={row.rowLabel} className="border-b border-panel-border/50">
      <td className="px-2 py-1.5 text-text-bright font-semibold">
        {row.rowLabel}
      </td>
      {row.cells.map((cell, colIdx) => {
        const isFirst = rowIdx === 0 && colIdx === 0 && cell.editable;
        const cellRef = isFirst ? firstInputRef : undefined;

        if (!cell.editable) {
          // D1: non-numeric column — read-only display
          return (
            <td
              key={cell.colKey}
              className="px-2 py-1.5 text-text-dim"
              data-testid={`cell-${row.rowLabel}-${cell.colKey}`}
            >
              {cell.displayValue !== undefined
                ? String(cell.displayValue)
                : '—'}
            </td>
          );
        }

        // An in-progress draft (including "") wins over the committed number so
        // the field can be empty/mid-typed while editing.
        const draft = cellDrafts[row.rowLabel]?.[cell.colKey];
        const editedVal = editedValues[row.rowLabel]?.[cell.colKey];
        const displayValue =
          draft !== undefined
            ? draft
            : editedVal !== undefined
            ? String(editedVal)
            : typeof cell.displayValue === 'number'
            ? String(cell.displayValue)
            : '';

        return (
          <td key={cell.colKey} className="px-2 py-1.5">
            <input
              ref={cellRef as React.Ref<HTMLInputElement>}
              type="number"
              step={
                cell.dataType === 'decimal' || cell.dataType === 'percentage'
                  ? 'any'
                  : undefined
              }
              value={displayValue}
              placeholder={
                typeof cell.currentValue === 'number'
                  ? formatCellDisplay(
                      cell.currentValue,
                      cell.dataType as DataType
                    )
                  : ''
              }
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                onNumericChange(row.rowLabel, cell.colKey, e.target.value)
              }
              className={clsx(
                'w-full bg-transparent border border-panel-border/40 rounded-[2px] px-1.5 py-0.5',
                'text-text-bright placeholder:text-text-dim',
                'focus:outline-none focus:border-accent/50 focus:bg-panel-bg'
              )}
              data-testid={`cell-${row.rowLabel}-${cell.colKey}`}
            />
          </td>
        );
      })}
    </tr>
  );
}

// ── Utility functions ─────────────────────────────────────────────────────────────

export function isNumericDataType(dataType: string): boolean {
  return [
    'integer',
    'decimal',
    'currency',
    'currency_per_sf',
    'currency_per_unit_year',
    'percentage',
    'months',
    'years',
    'number',
  ].includes(dataType);
}

/**
 * Per-dataType placeholder formatter. Covers all enum values.
 */
export function formatCellDisplay(
  value: number | string | boolean | undefined,
  dataType: DataType | string
): string {
  if (value === undefined || value === '') return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (dataType === 'string') return String(value);
  if (dataType === 'date') {
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return excelSerialToIsoDate(value);
    return '';
  }
  if (typeof value !== 'number') return String(value);
  if (
    dataType === 'currency' ||
    dataType === 'currency_per_sf' ||
    dataType === 'currency_per_unit_year'
  ) {
    return formatCurrency(value);
  }
  if (dataType === 'percentage') return formatPercent(value);
  if (dataType === 'months') return `${formatNumber(value)} mo`;
  if (dataType === 'years') return `${formatNumber(value)} yr`;
  if (dataType === 'decimal') return formatNumber(value, 2);
  return formatNumber(value);
}

/**
 * Converts an Excel serial date to an ISO `YYYY-MM-DD` string.
 */
export function excelSerialToIsoDate(serial: number): string {
  if (!Number.isFinite(serial) || serial < 1) return '';
  const epochUtcMs = Date.UTC(1899, 11, 30);
  const ms = epochUtcMs + serial * 86400000;
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}
